import React, { useEffect, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';

const WarningDialog = ({ onClose, onExtendSession, timeoutMinutes = 5, onTimeout }) => {
  const { t } = useTranslation();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [remainingSeconds, setRemainingSeconds] = useState(timeoutMinutes * 60);

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Escape') {
      onExtendSession(); // Default to extending session on Escape
    }
  }, [onExtendSession]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (onTimeout) {
            onTimeout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onTimeout]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 dark:bg-gray-900/75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 p-8 max-w-md w-full mx-4">
        <div className="flex items-center mb-6">
          <AlertTriangle className="h-8 w-8 text-yellow-500 mr-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('Session Timeout Warning')}
          </h2>
        </div>
        <div className="mb-8">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t('You will be logged out due to inactivity. Do you want to extend your session?')}
          </p>
          <div className="flex items-center justify-center">
            <div className="text-4xl font-bold text-red-600 tabular-nums">
              {formatTime(remainingSeconds)}
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150 ease-in-out"
          >
            <LogOut className="mr-2 -ml-1 h-5 w-5" />
            {t('Logout')}
          </button>
          <button
            onClick={onExtendSession}
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm dark:shadow-gray-900/20 text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
          >
            <RefreshCw className="mr-2 -ml-1 h-5 w-5" />
            {t('Extend Session')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarningDialog;